import { currentHumanReferenceText } from "./humanMarkdown";
import type { Issue } from "./types";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";

type Direction = "doc_to_issue" | "issue_to_doc";
export type DocIssueReference = { issue: Issue; kind: "explicit_reference"; directions: Direction[] };
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type ReferenceContext = { workspaceSlug: string; canonicalOrigin?: string; teamPrefixes?: readonly string[] };
const referenceMarkdown = unified().use(remarkParse).use(remarkGfm);
type ReferenceNode = { type: string; value?: string; children?: ReferenceNode[]; position?: { start: { offset?: number }; end: { offset?: number } } };
const nonProseNodes = new Set(["link", "linkReference", "image", "imageReference", "code", "inlineCode", "html", "definition"]);

/** Bare codes are meaningful only in a known workspace namespace. Parsing the
 * Markdown keeps nested destinations and link titles out of the prose surface. */
function bareIssueIdentifiers(text: string, teamPrefixes: readonly string[]): string[] {
  const prefixes = new Set(teamPrefixes.map((prefix) => prefix.toUpperCase()).filter((prefix) => /^[A-Z][A-Z0-9]*$/.test(prefix)));
  if (!prefixes.size) return [];
  const blank = (value: string) => value.replace(/[^\r\n]/g, " ");
  // Inline HTML is emitted as html/text/html siblings, so skipping html nodes
  // alone would treat their contents as ordinary Markdown prose.
  const htmlRanges = [...text.matchAll(/<([a-z][\w:-]*)\b[^>]*>[\s\S]*?<\/\1\s*>/gi)]
    .map((match) => ({ start: match.index!, end: match.index! + match[0].length }));
  const identifiers = new Set<string>();
  function visit(node: ReferenceNode) {
    if (nonProseNodes.has(node.type)) return;
    if (node.type === "text") {
      // Original text ranges retain Markdown escapes instead of turning an
      // escaped hyphen/entity into a newly meaningful identifier.
      const start = node.position?.start.offset, end = node.position?.end.offset;
      if (start !== undefined && end !== undefined && htmlRanges.some((range) => start < range.end && end > range.start)) return;
      const raw = start !== undefined && end !== undefined ? text.slice(start, end) : node.value ?? "";
      const prose = raw.replace(/\S*[\/\\@]\S*|\b(?:[a-z][a-z0-9+.-]*:|www\.)\S+/gi, blank);
      for (const match of prose.matchAll(/(?<![\p{L}\p{N}_@#\\/.-])([A-Z][A-Z0-9]*)-([1-9][0-9]*)(?![\p{L}\p{N}_@\\/-]|\.[\p{L}\p{N}])/giu)) {
        if (prefixes.has(match[1].toUpperCase())) identifiers.add(`${match[1].toUpperCase()}-${match[2]}`);
      }
    }
    node.children?.forEach(visit);
  }
  visit(referenceMarkdown.parse(text));
  return [...identifiers];
}

function canonicalTargets(text: string, entity: "doc" | "issue", { workspaceSlug, canonicalOrigin }: ReferenceContext): string[] {
  if (!workspaceSlug || !canonicalOrigin) return [];
  let origin: URL;
  try { origin = new URL(canonicalOrigin); } catch { return []; }
  if (!/^https?:$/.test(origin.protocol)) return [];
  const targets: string[] = [];
  // Never take a query parameter from a foreign origin or another workspace.
  for (const candidate of text.matchAll(/(?<![\w])https?:\/\/[^\s<>"')\]]+|(?<![\w/:])\/(?!\/)[^\s<>"')\]]+\?(?:[^\s<>"')\]]+)/g)) {
    try {
      const url = new URL(candidate[0], origin.origin);
      if (url.origin !== origin.origin || url.username || url.password || url.pathname !== `/${encodeURIComponent(workspaceSlug)}`) continue;
      if (url.searchParams.getAll(entity).length !== 1 || url.searchParams.has(entity === "doc" ? "issue" : "doc") || url.searchParams.has("note")) continue;
      const id = url.searchParams.get(entity)!;
      if (uuid.test(id)) targets.push(id.toLowerCase());
    } catch { /* Invalid links do not establish relationships. */ }
  }
  return targets;
}

/** Candidates for a scoped, authenticated lookup. A candidate is not an
 * established relationship until an accessible issue has been resolved. */
export function extractDocIssueTargets({ doc, ...context }: ReferenceContext & { doc: { content: string } }): { issueIds: string[]; identifiers: string[] } {
  const human = currentHumanReferenceText(doc.content);
  const issueIds = new Set(canonicalTargets(human, "issue", context));
  const identifiers = new Set<string>();
  for (const match of human.matchAll(/(?<![\w@\\])@([A-Z][A-Z0-9]*-[1-9][0-9]*)(?![\w-])/gi)) identifiers.add(match[1].toUpperCase());
  for (const match of human.matchAll(/(?<![\\!])\[[^\]\r\n]*\]\(issue:([^\s)]+)\)/gi)) {
    let target: string;
    try { target = decodeURIComponent(match[1]); } catch { continue; }
    if (uuid.test(target)) issueIds.add(target.toLowerCase());
    else if (/^[A-Z][A-Z0-9]*-[1-9][0-9]*$/i.test(target)) identifiers.add(target.toUpperCase());
  }
  for (const identifier of bareIssueIdentifiers(human, context.teamPrefixes ?? [])) identifiers.add(identifier);
  return { issueIds: [...issueIds], identifiers: [...identifiers] };
}

/** Only references to accessible entities in this workspace. Subject, tags,
 * project membership and machine context cannot establish a relationship. */
export function getDocIssueReferences({ doc, issues, workspaceSlug, canonicalOrigin, teamPrefixes }: {
  doc: { id: string; workspaceId: string; content: string };
  issues: Issue[];
  workspaceSlug: string;
  canonicalOrigin?: string;
  teamPrefixes?: readonly string[];
}): DocIssueReference[] {
  const accessible = issues.filter((issue) => issue.workspaceId === doc.workspaceId);
  const byId = new Map(accessible.map((issue) => [issue.id.toLowerCase(), issue]));
  const byIdentifier = new Map(accessible.map((issue) => [issue.identifier.toUpperCase(), issue]));
  const references = new Map<string, DocIssueReference>();
  function add(issue: Issue | undefined, direction: Direction) {
    if (!issue) return;
    const entry = references.get(issue.id) ?? { issue, kind: "explicit_reference" as const, directions: [] };
    if (!entry.directions.includes(direction)) entry.directions.push(direction);
    references.set(issue.id, entry);
  }
  const context = { workspaceSlug, canonicalOrigin, teamPrefixes };
  const targets = extractDocIssueTargets({ doc, ...context });
  for (const identifier of targets.identifiers) add(byIdentifier.get(identifier), "doc_to_issue");
  for (const id of targets.issueIds) add(byId.get(id), "doc_to_issue");
  for (const issue of accessible) {
    const humanIssue = currentHumanReferenceText(issue.description);
    const explicitDoc = [...humanIssue.matchAll(/(?<![\\!])\[[^\]\r\n]*\]\(doc:([^\s)]+)\)/gi)].some((match) => {
      try { return decodeURIComponent(match[1]).toLowerCase() === doc.id.toLowerCase(); } catch { return false; }
    });
    if (explicitDoc || canonicalTargets(humanIssue, "doc", context).includes(doc.id.toLowerCase())) add(issue, "issue_to_doc");
  }
  return [...references.values()];
}
